import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { CircleNotch, Coins, MagicWand } from "@phosphor-icons/react";
import { TOKEN_COSTS } from "@stylingcv/config";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from "@stylingcv/ui";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useTokenModal } from "@/client/contexts/token-modal-context";
import { useToast } from "@/client/hooks/use-toast";
import { generateSummary } from "@/client/services/openai/generate-summary";
import { useAuthStore } from "@/client/stores/auth";
import { useDialog } from "@/client/stores/dialog";
import { useResumeStore } from "@/client/stores/resume";
import { useCentralizedTokens } from "@/client/hooks/use-centralized-tokens";

const formSchema = z.object({
  jobTitle: z.string().min(1, "Job title is required"),
});

export const GenerateSummaryDialog = () => {
  const navigate = useNavigate();
  const { isOpen, payload, close } = useDialog<{ jobTitle: string }>("generate-summary");
  const { resume, setValue } = useResumeStore();
  const { tokenBalance, refreshTokens } = useCentralizedTokens();
  const isLoggedIn = useAuthStore((state) => !!state.user);
  const { toast } = useToast();

  // Use the global token modal context
  const { showTokenModal } = useTokenModal();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      jobTitle: payload?.item?.jobTitle || "",
    },
  });

  const loading = form.formState.isSubmitting;

  // Update form values when dialog opens with new payload
  useEffect(() => {
    if (isOpen && payload?.item?.jobTitle) {
      form.setValue("jobTitle", payload.item.jobTitle);
    }
  }, [isOpen, payload?.item?.jobTitle, form]);

  const onSubmit = async (data: z.infer<typeof formSchema>) => {
    if (!resume.data) return;

    const cost = TOKEN_COSTS.AI_SUMMARY;

    // Check if user has enough tokens BEFORE calling API
    if (tokenBalance < cost) {
      showTokenModal({
        requiredTokens: cost,
        currentTokens: tokenBalance,
        featureName: "AI Summary Generation",
        onNavigation: close, // Close this dialog when user navigates to pricing
      });
      return;
    }

    // NOTE: Token deduction is now handled by the backend (/ai/write/summary endpoint)
    // to avoid double deduction. We only do pre-check above for UX (show modal if insufficient).

    try {
      // Get the current resume language from metadata.locale
      const locale = resume.data.metadata.locale || "en-US";

      // Convert locale to language name for the AI prompt
      const getLanguageFromLocale = (locale: string): string => {
        const localeToLanguage: Record<string, string> = {
          "ar-SA": "العربية",
          "en-US": t`English`,
          "es-ES": t`Spanish`,
          "fr-FR": t`French`,
          "de-DE": t`German`,
          "pt-BR": t`Portuguese`,
          "it-IT": t`Italian`,
          "ru-RU": t`Russian`,
          "ja-JP": t`Japanese`,
          "ko-KR": t`Korean`,
          "zh-CN": t`Chinese`,
          "hi-IN": t`Hindi`,
          "tr-TR": t`Turkish`,
          "nl-NL": t`Dutch`,
          "sv-SE": t`Swedish`,
          "no-NO": t`Norwegian`,
          "da-DK": t`Danish`,
          "pl-PL": t`Polish`,
          "cs-CZ": t`Czech`,
          "hu-HU": t`Hungarian`,
        };

        return localeToLanguage[locale] || t`English`;
      };

      const resumeLanguage = getLanguageFromLocale(locale);

      const generatedSummary = await generateSummary(data.jobTitle, resumeLanguage);

      // Update the summary content
      setValue("sections.summary.content", generatedSummary);

      // Show success toast (tokens were deducted by backend)
      toast({
        variant: "default",
        title: t`Summary Generated`,
        description: t`Your professional summary has been generated successfully. ${cost} tokens used.`,
      });

      // Refresh token balance to reflect backend deduction
      await refreshTokens();

      close();
    } catch (error) {
      toast({
        variant: "error",
        title: t`Failed to Generate Summary`,
        description: error instanceof Error ? error.message : t`An unknown error occurred`,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MagicWand className="size-5 text-primary" />
            {t`Generate AI Summary`}
          </DialogTitle>
          <DialogDescription>
            {t`Generate a professional summary for your resume using AI. This will cost ${TOKEN_COSTS.AI_SUMMARY} tokens.`}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
            <FormField
              control={form.control}
              name="jobTitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t`Headline/Job Title`}</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={loading}
                      placeholder={t`e.g., Software Engineer, Marketing Manager`}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button disabled={loading} type="button" variant="outline" onClick={close}>
                {t`Cancel`}
              </Button>
              <Button disabled={loading} type="submit">
                {loading ? (
                  <>
                    <CircleNotch className="me-2 size-4 animate-spin" />
                    {t`Generating...`}
                  </>
                ) : (
                  <>
                    <MagicWand className="me-2 size-4" />
                    {t`Generate`}
                    <div className="ms-2 flex items-center">
                      <Coins className="me-1 text-yellow-200" size={14} />
                      <span className="text-xs">{TOKEN_COSTS.AI_SUMMARY}</span>
                    </div>
                  </>
                )}
              </Button>
            </DialogFooter>
            <div className="flex hidden items-center justify-between text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Coins className="text-yellow-500" size={14} />
                <span className="font-medium">
                  {tokenBalance.toLocaleString()} {t`tokens`}
                </span>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
